import {
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneAlert, ResourceHeader, Spinner } from '@factorialco/f0-react/dist/experimental'
import { FormMembershipDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  FormAnswerInline,
  FormResponsesInline,
  useAnswerForm,
  useDeleteAnswers,
} from 'modules/forms'
import {
  extractFormFromQuizBlock,
  populateFormWithAnswers,
} from 'modules/trainings/components/content/items/quiz/FormQuizHelper'
import {
  ContentConsumerCallbacks,
  ContentConsumerProps,
} from 'modules/trainings/components/content/types'
import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'

export const QuizBlockConsumer = forwardRef(function QuizBlockConsumer(
  { contentItem, updateNextButton }: ContentConsumerProps<QuizBlock>,
  ref: Ref<ContentConsumerCallbacks>
) {
  const [baseForm, setbaseForm] = useState(() => extractFormFromQuizBlock(contentItem))
  useEffect(() => {
    setbaseForm(extractFormFromQuizBlock(contentItem))
  }, [contentItem])

  const { access } = useCurrentSession()
  const { saveAnswers, savingAnswers } = useAnswerForm()
  const { deleteAnswers } = useDeleteAnswers()

  const { data: membershipData } = useQuery(FormMembershipDocument, {
    variables: {
      formId: baseForm?.id || '',
      respondentId: access?.id?.toString() || '',
    },
    skip: !baseForm?.id || !access?.id,
  })
  const membershipId = membershipData?.forms.membershipsConnection.nodes?.[0]?.id
  const answers = useMemo(
    () => membershipData?.forms.membershipsConnection.nodes?.[0]?.answers || [],
    [membershipData]
  )
  const hasAnswers = answers.length > 0
  const score = Number(membershipData?.forms.membershipsConnection.nodes?.[0]?.score)
  const hasToRetry = hasAnswers && score != 100
  const isCompleted = hasAnswers && score == 100

  // Populate form with existing answers when available
  const form = useMemo(() => {
    if (baseForm && hasAnswers && answers.length > 0) {
      populateFormWithAnswers(baseForm, answers)
    }
    return baseForm
  }, [baseForm, hasAnswers, answers])

  const handleSubmit = useCallback<() => Promise<boolean>>(async () => {
    if (hasToRetry) {
      return false
    }

    if (isCompleted) {
      return true
    }

    if (!form || !membershipId) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
      return false
    }

    if (form.hasUnansweredMandatoryElements()) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('forms.render.submit_mandatory_pending'),
      })
      return false
    }

    await saveAnswers({
      form,
      membershipId,
      onSuccess: (answersFromMutation) => {
        // Populate form with answers returned from mutation (includes isCorrect)
        if (form && answersFromMutation && answersFromMutation.length > 0) {
          populateFormWithAnswers(form, answersFromMutation)
        }

        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.content.consumer.quiz.submitted'),
        })

        updateNextButton()
      },
    })

    // Always return false to prevent continue after saving the answers.
    // The button will be updated when the mutation is resolved and you can continue.
    return false
  }, [form, membershipId, saveAnswers, hasToRetry, isCompleted, updateNextButton])

  useImperativeHandle(
    ref,
    () => {
      return {
        handleNext: handleSubmit,
        nextEnabled: () => {
          return !hasToRetry && !!form && !form.hasUnansweredMandatoryElements() //!!form because form can be undefined
        },
        nextLabel: () => {
          return !isCompleted ? i18n.t('common.submit') : ''
        },
      }
    },
    [handleSubmit, hasToRetry, form, isCompleted]
  )

  const metadata = useMemo(
    () => [
      {
        // TODO: fix status tag
        label: '',
        value: {
          type: 'tag-list' as const,
          tags: [i18n.t('trainings.content.consumer.quiz.status.not_started')],
        },
      },
      {
        label: i18n.t('trainings.content.consumer.quiz.questions.label'),
        value: {
          type: 'text' as const,
          content: form?.elements.length.toString() || '0',
        },
      },
    ],
    [form?.elements.length]
  )

  if (!form) return null

  if (savingAnswers) {
    return (
      <Box gap="s8" alignSelf="center" alignItems="center" height="full">
        <Spinner size="large" className="text-f1-icon-info" />
        <Text.Normal weight="bold">
          {i18n.t('trainings.content.consumer.quiz.saving.title')}
        </Text.Normal>
        <Text.Normal size="200">
          {i18n.t('trainings.content.consumer.quiz.saving.subtitle')}
        </Text.Normal>
      </Box>
    )
  }

  return (
    <Box width="full" gap="s24">
      {hasToRetry && (
        <OneAlert
          variant="critical"
          title={i18n.t('trainings.content.consumer.quiz.did_not_pass')}
          description={''}
          action={{
            label: i18n.t('trainings.content.consumer.quiz.retry'),
            onClick: async () => {
              if (membershipId) {
                await deleteAnswers({
                  membershipId,
                  onSuccess: () => {
                    form.resetFormAnswers()
                    updateNextButton()
                  },
                })
              }
            },
          }}
        />
      )}
      {isCompleted && (
        <OneAlert
          variant="positive"
          title={i18n.t('trainings.content.consumer.quiz.passed')}
          description={''}
        />
      )}
      <ResourceHeader
        title={contentItem.title}
        description={form?.description || undefined}
        metadata={metadata}
      />
      {hasAnswers ? (
        <FormResponsesInline form={form} />
      ) : (
        <FormAnswerInline
          form={form}
          onFormUpdated={() => {
            // form.elements.forEach((e) => {
            //   console.log(JSON.stringify(e.answer))
            // })
            updateNextButton()
          }}
        />
      )}
    </Box>
  )
})
